import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { EXTERNAL_COS_BUCKET } from '@/util/constants';

/**
 * Available waiting sound types that can be played during voice calls.
 * These sounds are stored in the S3/Minio bucket at `media/sounds/{name}.mp3`
 */
export const WAITING_SOUND_NAMES = [
  'gentle_knock',
  'muted_tick',
  'soft_doubletap',
  'soft_pop',
  'warm_pulse',
] as const;

export type WaitingSoundName = (typeof WAITING_SOUND_NAMES)[number];

export type WaitingSound = {
  /** Unique identifier for the sound */
  id: WaitingSoundName;
  /** Display name for UI */
  name: string;
  /** URL to the MP3 file in S3/Minio */
  url: string;
};

/**
 * Constructs the full URL for a waiting sound file.
 * @param soundName - The name of the sound file (without extension)
 * @returns The full HTTPS URL to the sound file
 */
export const getWaitingSoundUrl = (soundName: WaitingSoundName): string => {
  return `https://${EXTERNAL_COS_BUCKET}/media/sounds/${soundName}.mp3`;
};

/**
 * Formats a sound name for display (e.g., 'soft_pop' -> 'Soft Pop')
 */
const formatSoundName = (name: WaitingSoundName): string => {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Hook for managing waiting sounds used in voice integrations (e.g., Twilio).
 * These sounds indicate processing status to users during voice calls.
 *
 * @example
 * ```tsx
 * const { sounds, playSound, stopSound, isPlaying } = useWaitingSounds();
 *
 * // Play a sound
 * playSound('soft_pop');
 *
 * // Stop currently playing sound
 * stopSound();
 *
 * // Check if a specific sound is playing
 * if (isPlaying === 'soft_pop') { ... }
 * ```
 */
const useWaitingSounds = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isPlaying, setIsPlaying] = useState<WaitingSoundName | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * List of all available waiting sounds with their URLs
   */
  const sounds: WaitingSound[] = useMemo(
    () =>
      WAITING_SOUND_NAMES.map((name) => ({
        id: name,
        name: formatSoundName(name),
        url: getWaitingSoundUrl(name),
      })),
    []
  );

  /**
   * Stops the currently playing sound and cleans up resources
   */
  const stopSound = useCallback(() => {
    // Abort event listeners before cleaning up audio
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setIsPlaying(null);
    setIsLoading(false);
  }, []);

  /**
   * Cleanup on unmount - stop any playing audio and remove event listeners
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  /**
   * Plays a waiting sound by name
   * @param soundName - The name of the sound to play
   * @param loop - Whether to loop the sound (default: false)
   */
  const playSound = useCallback(
    (soundName: WaitingSoundName, loop = false) => {
      // Stop any currently playing sound and clean up previous listeners
      stopSound();

      const url = getWaitingSoundUrl(soundName);
      const audio = new Audio();
      audio.src = url;
      audio.loop = loop;
      audioRef.current = audio;

      // Create new AbortController for this audio's event listeners
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const { signal } = abortController;

      setIsLoading(true);

      audio.addEventListener(
        'canplaythrough',
        () => {
          setIsLoading(false);
        },
        { signal }
      );

      audio.addEventListener(
        'ended',
        () => {
          if (!loop) {
            setIsPlaying(null);
            audioRef.current = null;
          }
        },
        { signal }
      );

      audio.addEventListener(
        'error',
        (e) => {
          console.error('Error playing waiting sound:', e);
          setIsLoading(false);
          setIsPlaying(null);
          audioRef.current = null;
        },
        { signal }
      );

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(soundName);
          })
          .catch((error) => {
            console.error('Error playing waiting sound:', error);
            setIsLoading(false);
            setIsPlaying(null);
            audioRef.current = null;
          });
      }
    },
    [stopSound]
  );

  /**
   * Preloads a sound to reduce latency when playing
   * @param soundName - The name of the sound to preload
   */
  const preloadSound = useCallback((soundName: WaitingSoundName) => {
    const url = getWaitingSoundUrl(soundName);
    const audio = new Audio();
    audio.preload = 'auto';
    audio.src = url;
  }, []);

  /**
   * Preloads all waiting sounds
   */
  const preloadAllSounds = useCallback(() => {
    WAITING_SOUND_NAMES.forEach(preloadSound);
  }, [preloadSound]);

  /**
   * Gets a specific sound by name
   */
  const getSound = useCallback(
    (soundName: WaitingSoundName): WaitingSound | undefined => {
      return sounds.find((s) => s.id === soundName);
    },
    [sounds]
  );

  return {
    /** List of all available waiting sounds */
    sounds,
    /** Currently playing sound name, or null if none */
    isPlaying,
    /** Whether a sound is currently loading */
    isLoading,
    /** Play a sound by name */
    playSound,
    /** Stop the currently playing sound */
    stopSound,
    /** Preload a specific sound */
    preloadSound,
    /** Preload all sounds */
    preloadAllSounds,
    /** Get a sound by name */
    getSound,
  };
};

export default useWaitingSounds;
